import moment, { Moment } from 'moment'

import { GetterTree } from 'vuex'

import { Invoice, InvoiceStatus, Order } from '@/models'

import { orderByDateField, groupByDay } from '@/utils'

import { RootState } from '../../state'
import { OrderingState } from '../state'

const getters: GetterTree<OrderingState, RootState> = {
  /**
   * Returns an Invoice by its id, or null if not found.
   *
   * @param id Invoice id to be found.
   */
  invoiceById: (state) => (id: number): Invoice | null => {
    return state.invoices.elements.find((i) => i.id === id) || null
  },

  /**
   * Returns the last time an Invoice status changed.
   *
   * @param invoice Invoice whose last status time will be returned.
   */
  invoiceLastUpdate: () => (invoice: Invoice): Date => {
    return (
      invoice.closedAt ||
      invoice.closingAt ||
      invoice.createdAt
    )
  },

  /**
   * Returns a list of location names for display.
   */
  invoiceLocations: (_, getters) => (invoice: Invoice): string[] | null => {
    const orders: Order[] = getters.ordersByInvoiceId(invoice.id)

    return orders.length > 0
      ? getters.orderLocations(orders[0])
      : null
  },

  /**
   * Returns Invoices filtered with current query.
   *
   * It must replicate the API filtering to avoid mismatching the total
   * and reloading the pages infinitely.
   */
  filteredInvoices (state): Invoice[] {
    const query = state.invoices.query

    const fromDate = query.from ? moment.utc(query.from).startOf('day') : null
    const untilDate = query.until ? moment.utc(query.until).endOf('day') : null
    const status = query.status
    const paymentMethods = query.paymentMethods

    let filtered = state.invoices.elements

    if (fromDate !== null) {
      filtered = filtered.filter((invoice) => (fromDate.isSameOrBefore(invoice.createdAt)))
    }

    if (untilDate !== null) {
      filtered = filtered.filter((invoice) => (untilDate.isSameOrAfter(invoice.createdAt)))
    }

    if (status && status.length > 0) {
      const includeOpen = status.includes(InvoiceStatus.OPEN)
      const includeClosing = status.includes(InvoiceStatus.CLOSING)
      const includeClosed = status.includes(InvoiceStatus.CLOSED)

      filtered = filtered.filter((invoice) => (
        (includeOpen && invoice.closingAt == null && invoice.closedAt == null) ||
        (includeClosing && invoice.closingAt != null && invoice.closedAt == null) ||
        (includeClosed && invoice.closedAt != null)
      ))
    }

    if (paymentMethods && paymentMethods.length > 0) {
      filtered = filtered.filter((invoice) => invoice.paymentMethod && paymentMethods.includes(invoice.paymentMethod))
    }

    return filtered
  },

  /**
   * Returns Invoices ordered by createdAt.
   */
  orderedInvoices (_, getters): Invoice[] {
    return orderByDateField(getters.filteredInvoices, 'createdAt')
  },

  /**
   * Returns Invoices grouped by their `createdAt`.
   *
   * The groups are in descending order.
   */
  invoiceGroupsByDay (_, getters): [Moment, Invoice[]][] {
    return groupByDay(getters.orderedInvoices, 'createdAt')
  }
}

export default getters
